import React, { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { getItemWithExpiration } from '../../lib/utils';
import { useSuperAdminTeams, useSuperAdminTeamBreakdown } from '@/features/team';
import type { SuperAdminTeamRow, SuperAdminTeamBreakdownMember } from '@/features/team';

type TeamSortKey = 'memberCount' | 'total' | 'refund' | 'avgPerMember';
type MemberSortKey = 'total' | 'refund';
type SortDir = 'asc' | 'desc';

const T = {
    bg: '#F5F7FA',
    card: '#FFFFFF',
    cardBorder: '#E2E8F0',
    cardShadow: '0 1px 3px rgba(26,48,93,0.06), 0 1px 2px rgba(26,48,93,0.04)',
    navy: '#1A305D',
    blue: '#3D73DD',
    blueBg: '#ECF1FC',
    text1: '#1A305D',
    text2: '#5E7093',
    text3: '#8896B0',
    border: '#E2E8F0',
    error: '#FF3E4A',
    success: '#00A793',
};

const formatKRW = (n: number) => `${(n ?? 0).toLocaleString()}원`;

interface Props {
    /** 슈퍼관리자 헤더 글로벌 토글 — true면 그룹수업 매출도 합산 */
    includeGroup?: boolean;
}

const SuperAdminTeamAnalyticsPage = ({ includeGroup = false }: Props) => {
    const authObject = getItemWithExpiration('authObject');
    const superAdminId: string = authObject?.superAdminId || authObject?.adminId || '';

    // 기본 기간: 이번 달
    const today = dayjs();
    const [from, setFrom] = useState(today.startOf('month').format('YYYY-MM-DD'));
    const [to, setTo] = useState(today.endOf('month').format('YYYY-MM-DD'));
    const [includeDeleted, setIncludeDeleted] = useState(false);

    const fromIso = useMemo(() => dayjs(from).startOf('day').toISOString(), [from]);
    const toIso = useMemo(() => dayjs(to).endOf('day').toISOString(), [to]);

    const { data, isLoading } = useSuperAdminTeams({
        superAdminId,
        from: fromIso,
        to: toIso,
        includeDeleted,
        includeGroup,
    });

    const [selectedTeam, setSelectedTeam] = useState<SuperAdminTeamRow | null>(null);

    // 정렬 — 기본 총매출 desc
    const [sortKey, setSortKey] = useState<TeamSortKey>('total');
    const [sortDir, setSortDir] = useState<SortDir>('desc');

    const onSort = (key: TeamSortKey) => {
        if (sortKey === key) {
            setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortKey(key);
            setSortDir('desc');
        }
    };

    const sortedTeams = useMemo(() => {
        if (!data?.teams) return [];
        const rows = [...data.teams];
        rows.sort((a, b) => {
            const av =
                sortKey === 'memberCount'
                    ? a.memberCount
                    : sortKey === 'total'
                      ? a.revenue.total
                      : sortKey === 'refund'
                        ? a.revenue.refund
                        : a.avgPerMember;
            const bv =
                sortKey === 'memberCount'
                    ? b.memberCount
                    : sortKey === 'total'
                      ? b.revenue.total
                      : sortKey === 'refund'
                        ? b.revenue.refund
                        : b.avgPerMember;
            return sortDir === 'asc' ? av - bv : bv - av;
        });
        return rows;
    }, [data?.teams, sortKey, sortDir]);

    return (
        <div style={{ padding: 32, background: T.bg, minHeight: '100vh' }}>
            <div style={{ marginBottom: 28, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <h1 style={{ fontSize: 28, fontWeight: 700, color: T.navy, margin: 0 }}>PT팀 매출 비교</h1>
                    <p style={{ color: T.text2, marginTop: 6, fontSize: 15 }}>
                        프랜차이즈 전체 팀의 매출과 인당 매출을 비교 분석합니다. (read-only)
                    </p>
                </div>
                <div
                    style={{
                        padding: '10px 16px',
                        background: includeGroup ? T.blueBg : '#F8FAFC',
                        border: `1px solid ${includeGroup ? '#10B981' : T.border}`,
                        borderRadius: 8,
                        fontSize: 14,
                        color: includeGroup ? '#065F46' : T.text2,
                        fontWeight: 500,
                    }}
                >
                    그룹수업 매출 {includeGroup ? 'ON' : 'OFF'} — 헤더 토글로 변경
                </div>
            </div>

            {/* 필터 */}
            <div
                style={{
                    background: T.card,
                    border: `1px solid ${T.border}`,
                    borderRadius: 12,
                    padding: 20,
                    marginBottom: 24,
                    display: 'flex',
                    gap: 20,
                    alignItems: 'flex-end',
                }}
            >
                <div>
                    <label style={{ fontSize: 14, color: T.text2, display: 'block', marginBottom: 6 }}>시작일</label>
                    <input
                        type="date"
                        value={from}
                        onChange={(e) => setFrom(e.target.value)}
                        style={{ padding: '10px 12px', border: `1px solid ${T.border}`, borderRadius: 6, fontSize: 15 }}
                    />
                </div>
                <div>
                    <label style={{ fontSize: 14, color: T.text2, display: 'block', marginBottom: 6 }}>종료일</label>
                    <input
                        type="date"
                        value={to}
                        onChange={(e) => setTo(e.target.value)}
                        style={{ padding: '10px 12px', border: `1px solid ${T.border}`, borderRadius: 6, fontSize: 15 }}
                    />
                </div>
                <label
                    style={{
                        fontSize: 15,
                        color: T.text2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '10px 14px',
                        border: `1px solid ${T.border}`,
                        borderRadius: 6,
                        background: T.card,
                        cursor: 'pointer',
                        userSelect: 'none',
                    }}
                >
                    <input
                        type="checkbox"
                        checked={includeDeleted}
                        onChange={(e) => setIncludeDeleted(e.target.checked)}
                        style={{ width: 16, height: 16, cursor: 'pointer' }}
                    />
                    해체 포함
                </label>
            </div>

            {/* 요약 카드 */}
            {isLoading ? (
                <div style={{ color: T.text2, fontSize: 16 }}>로딩 중...</div>
            ) : !data ? (
                <div style={{ color: T.text2, fontSize: 16 }}>데이터 없음</div>
            ) : data.teams.length === 0 ? (
                <SuperAdminIntroGuide />
            ) : (
                <>
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gap: 16,
                            marginBottom: 24,
                        }}
                    >
                        <SummaryCard
                            label={includeGroup ? '센터 총 PT+그룹 매출' : '센터 총 PT 매출'}
                            value={formatKRW(data.totalCenterRevenue)}
                            sub="(전체 결제)"
                        />
                        <SummaryCard
                            label="팀 합산 매출"
                            value={formatKRW(data.totalTeamRevenue)}
                            sub={`${((data.totalTeamRevenue / Math.max(1, data.totalCenterRevenue)) * 100).toFixed(1)}%`}
                            color={T.blue}
                        />
                        <SummaryCard
                            label="미소속 매출"
                            value={formatKRW(data.unassignedRevenue)}
                            sub="(팀 미배치 판매자)"
                            color={T.text3}
                        />
                    </div>

                    <div style={{ marginBottom: 16, fontSize: 14, color: T.text3 }}>
                        ※ 센터 총매출 = 팀 합산 + 미소속 매출. {includeGroup ? 'PT + 그룹수업' : 'PT'} 결제만 포함.
                    </div>

                    {/* 팀 비교 표 */}
                    <div
                        style={{
                            background: T.card,
                            border: `1px solid ${T.border}`,
                            borderRadius: 12,
                            overflow: 'hidden',
                        }}
                    >
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead style={{ background: T.blueBg }}>
                                <tr>
                                    <Th>순위</Th>
                                    <Th>팀명</Th>
                                    <Th>팀장</Th>
                                    <SortTh
                                        active={sortKey === 'memberCount'}
                                        dir={sortDir}
                                        onClick={() => onSort('memberCount')}
                                    >
                                        인원
                                    </SortTh>
                                    <SortTh
                                        active={sortKey === 'total'}
                                        dir={sortDir}
                                        onClick={() => onSort('total')}
                                        align="right"
                                    >
                                        총 매출
                                    </SortTh>
                                    <SortTh
                                        active={sortKey === 'refund'}
                                        dir={sortDir}
                                        onClick={() => onSort('refund')}
                                        align="right"
                                    >
                                        환불
                                    </SortTh>
                                    <SortTh
                                        active={sortKey === 'avgPerMember'}
                                        dir={sortDir}
                                        onClick={() => onSort('avgPerMember')}
                                        align="right"
                                    >
                                        인당 매출
                                    </SortTh>
                                    <Th>&nbsp;</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedTeams.length === 0 ? (
                                    <tr>
                                        <td
                                            colSpan={8}
                                            style={{ padding: 24, textAlign: 'center', color: T.text2 }}
                                        >
                                            해당 기간에 팀 매출 데이터가 없습니다.
                                        </td>
                                    </tr>
                                ) : (
                                    sortedTeams.map((row, i) => {
                                        const showTrophy = sortKey === 'total' && sortDir === 'desc';
                                        const trophy = showTrophy && i === 0 ? '🥇' : showTrophy && i === 1 ? '🥈' : showTrophy && i === 2 ? '🥉' : null;
                                        return (
                                        <tr
                                            key={row.teamId}
                                            style={{ borderTop: `1px solid ${T.border}` }}
                                        >
                                            <Td>
                                                {trophy ? (
                                                    <span style={{ fontSize: 22 }}>{trophy}</span>
                                                ) : (
                                                    <span style={{ fontSize: 16, fontWeight: 600, color: T.text2 }}>
                                                        {i + 1}
                                                    </span>
                                                )}
                                            </Td>
                                            <Td>
                                                <span style={{ fontWeight: 600, color: T.navy, fontSize: 16 }}>
                                                    {row.name}
                                                </span>
                                                {!row.isActive && (
                                                    <span style={{ color: T.text3, marginLeft: 8, fontSize: 13 }}>
                                                        (해체)
                                                    </span>
                                                )}
                                            </Td>
                                            <Td>
                                                {row.leaderName ? (
                                                    <span style={{ color: T.text1 }}>{row.leaderName}</span>
                                                ) : (
                                                    <span style={{ color: T.text3, fontStyle: 'italic' }}>
                                                        (이름 없음)
                                                    </span>
                                                )}
                                            </Td>
                                            <Td>{row.memberCount}명</Td>
                                            <Td align="right">
                                                <span style={{ fontWeight: 700, color: T.blue, fontSize: 16 }}>
                                                    {formatKRW(row.revenue.total)}
                                                </span>
                                            </Td>
                                            <Td align="right">
                                                <span style={{ color: T.error, fontSize: 15 }}>
                                                    {formatKRW(row.revenue.refund)}
                                                </span>
                                            </Td>
                                            <Td align="right">{formatKRW(Math.round(row.avgPerMember))}</Td>
                                            <Td>
                                                <button
                                                    onClick={() => setSelectedTeam(row)}
                                                    style={{
                                                        padding: '4px 10px',
                                                        border: `1px solid ${T.blue}`,
                                                        background: T.card,
                                                        color: T.blue,
                                                        borderRadius: 5,
                                                        fontSize: 12,
                                                        fontWeight: 500,
                                                        cursor: 'pointer',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    자세히
                                                </button>
                                            </Td>
                                        </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {/* 상세 모달 */}
            {selectedTeam && (
                <BreakdownModal
                    superAdminId={superAdminId}
                    teamId={selectedTeam.teamId}
                    teamName={selectedTeam.name}
                    from={fromIso}
                    to={toIso}
                    includeGroup={includeGroup}
                    onClose={() => setSelectedTeam(null)}
                />
            )}
        </div>
    );
};

const SummaryCard = ({
    label,
    value,
    sub,
    color = T.navy,
}: {
    label: string;
    value: string;
    sub?: string;
    color?: string;
}) => (
    <div
        style={{
            background: T.card,
            border: `1px solid ${T.border}`,
            borderRadius: 12,
            padding: '24px 24px',
            boxShadow: T.cardShadow,
        }}
    >
        <div style={{ fontSize: 14, color: T.text2, marginBottom: 10 }}>{label}</div>
        <div style={{ fontSize: 28, fontWeight: 700, color, marginBottom: 6 }}>{value}</div>
        {sub && <div style={{ fontSize: 13, color: T.text3 }}>{sub}</div>}
    </div>
);

const Th = ({
    children,
    align,
}: {
    children: React.ReactNode;
    align?: 'left' | 'right' | 'center';
}) => (
    <th
        style={{
            padding: '14px 16px',
            textAlign: align ?? 'left',
            fontSize: 14,
            color: T.text2,
            fontWeight: 600,
        }}
    >
        {children}
    </th>
);

const Td = ({
    children,
    align,
}: {
    children: React.ReactNode;
    align?: 'left' | 'right' | 'center';
}) => (
    <td style={{ padding: '14px 16px', textAlign: align ?? 'left', fontSize: 15, color: T.text1 }}>
        {children}
    </td>
);

const SortTh = ({
    children,
    active,
    dir,
    onClick,
    align,
}: {
    children: React.ReactNode;
    active: boolean;
    dir: SortDir;
    onClick: () => void;
    align?: 'left' | 'right' | 'center';
}) => (
    <th
        onClick={onClick}
        style={{
            padding: '14px 16px',
            textAlign: align ?? 'left',
            fontSize: 14,
            color: active ? T.blue : T.text2,
            fontWeight: 600,
            cursor: 'pointer',
            userSelect: 'none',
        }}
    >
        <span
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
            }}
        >
            {children}
            <span style={{ fontSize: 11, opacity: active ? 1 : 0.35 }}>
                {active ? (dir === 'asc' ? '▲' : '▼') : '↕'}
            </span>
        </span>
    </th>
);

const BreakdownModal = ({
    superAdminId,
    teamId,
    teamName,
    from,
    to,
    includeGroup,
    onClose,
}: {
    superAdminId: string;
    teamId: string;
    teamName: string;
    from: string;
    to: string;
    includeGroup: boolean;
    onClose: () => void;
}) => {
    const { data, isLoading } = useSuperAdminTeamBreakdown({
        superAdminId,
        teamId,
        from,
        to,
        includeGroup,
    });

    // 모달 내부 정렬 — 기본 매출 desc
    const [sortKey, setSortKey] = useState<MemberSortKey>('total');
    const [sortDir, setSortDir] = useState<SortDir>('desc');
    const onSort = (key: MemberSortKey) => {
        if (sortKey === key) {
            setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortKey(key);
            setSortDir('desc');
        }
    };

    const sortedMembers = useMemo<SuperAdminTeamBreakdownMember[]>(() => {
        if (!data?.members) return [];
        const rows = [...data.members];
        rows.sort((a, b) => {
            const av = sortKey === 'total' ? a.revenue.total : a.revenue.refund;
            const bv = sortKey === 'total' ? b.revenue.total : b.revenue.refund;
            return sortDir === 'asc' ? av - bv : bv - av;
        });
        return rows;
    }, [data?.members, sortKey, sortDir]);

    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    background: T.card,
                    borderRadius: 14,
                    width: 760,
                    maxHeight: '85vh',
                    overflow: 'auto',
                    padding: 32,
                    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: 24,
                    }}
                >
                    <div>
                        <h2 style={{ margin: 0, fontSize: 22, color: T.navy, fontWeight: 700 }}>
                            {teamName}
                        </h2>
                        <div style={{ marginTop: 6, fontSize: 15, color: T.text3 }}>
                            팀원별 매출 자세히 보기
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            border: 'none',
                            background: 'none',
                            fontSize: 28,
                            cursor: 'pointer',
                            color: T.text3,
                            lineHeight: 1,
                        }}
                    >
                        ×
                    </button>
                </div>

                {isLoading || !data ? (
                    <div style={{ color: T.text2, padding: 24, textAlign: 'center' }}>로딩 중...</div>
                ) : (
                    <>
                        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 28 }}>
                            <thead style={{ background: T.blueBg }}>
                                <tr>
                                    <Th>이름</Th>
                                    <Th>타입</Th>
                                    <Th>역할</Th>
                                    <SortTh
                                        active={sortKey === 'total'}
                                        dir={sortDir}
                                        onClick={() => onSort('total')}
                                        align="right"
                                    >
                                        매출
                                    </SortTh>
                                    <SortTh
                                        active={sortKey === 'refund'}
                                        dir={sortDir}
                                        onClick={() => onSort('refund')}
                                        align="right"
                                    >
                                        환불
                                    </SortTh>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedMembers.map((m) => (
                                    <tr
                                        key={m.userId}
                                        style={{ borderTop: `1px solid ${T.border}` }}
                                    >
                                        <Td>
                                            {m.userName ? (
                                                <span style={{ fontWeight: 600, fontSize: 15 }}>{m.userName}</span>
                                            ) : (
                                                <span style={{ color: T.text3, fontStyle: 'italic', fontSize: 14 }}>
                                                    (이름 없음)
                                                </span>
                                            )}
                                        </Td>
                                        <Td>
                                            <span
                                                style={{
                                                    padding: '4px 12px',
                                                    background: m.userType === 'coach' ? T.blueBg : '#F1F5F9',
                                                    color: m.userType === 'coach' ? T.blue : T.text2,
                                                    borderRadius: 12,
                                                    fontSize: 13,
                                                    fontWeight: 500,
                                                }}
                                            >
                                                {m.userType === 'coach' ? '강사' : '판매담당자'}
                                            </span>
                                        </Td>
                                        <Td>
                                            {m.role === 'leader' ? (
                                                <span style={{ color: T.blue, fontWeight: 600, fontSize: 15 }}>팀장</span>
                                            ) : (
                                                <span style={{ fontSize: 15 }}>팀원</span>
                                            )}
                                        </Td>
                                        <Td align="right">
                                            <span style={{ fontWeight: 700, fontSize: 16 }}>
                                                {formatKRW(m.revenue.total)}
                                            </span>
                                        </Td>
                                        <Td align="right">
                                            <span style={{ color: T.error, fontSize: 15 }}>
                                                {formatKRW(m.revenue.refund)}
                                            </span>
                                        </Td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <h3 style={{ fontSize: 17, color: T.navy, marginTop: 12, marginBottom: 14, fontWeight: 700 }}>
                            일별 매출
                        </h3>
                        {data.daily.length === 0 ? (
                            <div
                                style={{
                                    padding: '28px 16px',
                                    background: '#F8FAFC',
                                    border: `1px dashed ${T.border}`,
                                    borderRadius: 8,
                                    color: T.text2,
                                    textAlign: 'center',
                                    fontSize: 15,
                                }}
                            >
                                해당 기간에 박제된 매출 데이터가 없습니다.
                            </div>
                        ) : (
                            (() => {
                                const max = Math.max(...data.daily.map((d) => Math.abs(d.revenue)), 1);
                                return (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        {data.daily.map((d) => {
                                            const pct = (Math.abs(d.revenue) / max) * 100;
                                            const isNegative = d.revenue < 0;
                                            return (
                                                <div
                                                    key={d.date}
                                                    style={{
                                                        display: 'grid',
                                                        gridTemplateColumns: '90px 1fr 150px',
                                                        alignItems: 'center',
                                                        gap: 14,
                                                    }}
                                                >
                                                    <span style={{ fontSize: 14, color: T.text2 }}>
                                                        {d.date.slice(5)}
                                                    </span>
                                                    <div
                                                        style={{
                                                            height: 26,
                                                            background: '#F1F5F9',
                                                            borderRadius: 4,
                                                            position: 'relative',
                                                            overflow: 'hidden',
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                position: 'absolute',
                                                                top: 0,
                                                                left: 0,
                                                                height: '100%',
                                                                width: `${pct}%`,
                                                                background: isNegative ? T.error : T.blue,
                                                                borderRadius: 4,
                                                                transition: 'width 0.3s',
                                                            }}
                                                        />
                                                    </div>
                                                    <span
                                                        style={{
                                                            fontSize: 15,
                                                            fontWeight: 700,
                                                            color: isNegative ? T.error : T.navy,
                                                            textAlign: 'right',
                                                        }}
                                                    >
                                                        {formatKRW(d.revenue)}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })()
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

const SuperAdminIntroGuide = () => (
    <div
        style={{
            background: 'linear-gradient(135deg, #ECF1FC 0%, #FFFFFF 60%)',
            border: `1px solid #C3D4F4`,
            borderRadius: 16,
            padding: '48px 56px',
        }}
    >
        <div style={{ marginBottom: 28 }}>
            <span style={{ fontSize: 40 }}>🏆</span>
            <h2
                style={{
                    margin: '12px 0 8px',
                    fontSize: 26,
                    fontWeight: 700,
                    color: T.navy,
                }}
            >
                아직 등록된 PT팀이 없습니다
            </h2>
            <p style={{ color: T.text2, lineHeight: 1.7, fontSize: 15, maxWidth: 760 }}>
                <b>PT팀 매출 비교</b>는 각 센터의 강사·판매담당자를 팀으로 묶어
                팀별 매출을 실시간 비교 분석하는 본사 전용 도구입니다. 센터 관리자가
                자신의 센터에서 팀을 생성하면 이 페이지에 자동으로 집계됩니다.
            </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 28 }}>
            <SuperAdminGuideStep
                num={1}
                title="센터 관리자가 팀 생성"
                desc={'각 센터의 관리자가 자신의 센터에서\n팀장·팀원을 배치해 PT팀을 생성합니다.'}
            />
            <SuperAdminGuideStep
                num={2}
                title="결제 시점부터 자동 합산"
                desc={'팀 생성 후 발생하는 PT+그룹수업 결제가\n자동으로 팀 매출에 박제됩니다.'}
            />
            <SuperAdminGuideStep
                num={3}
                title="본사에서 실시간 비교"
                desc={'이 화면에서 모든 센터의 팀 매출을\n순위·인당매출까지 한눈에 분석합니다.'}
            />
        </div>

        <div
            style={{
                padding: 20,
                background: T.card,
                border: `1px solid ${T.border}`,
                borderRadius: 10,
            }}
        >
            <div style={{ fontWeight: 600, fontSize: 15, color: T.navy, marginBottom: 10 }}>
                💡 도입을 안내하려면
            </div>
            <ul style={{ color: T.text2, fontSize: 14, lineHeight: 1.8, margin: 0, paddingLeft: 20 }}>
                <li>
                    각 센터 관리자에게 <b>강사 관리 → "PT팀 관리"</b> 진입을 안내해주세요.
                </li>
                <li>
                    팀 생성 시 <b>"집계 시작일"</b>을 과거 날짜로 지정하면 그 시점부터의 결제가
                    소급 합산됩니다 — 즉, 기존 매출도 팀에 묶어볼 수 있습니다.
                </li>
                <li>
                    상단의 <b>그룹수업 ON/OFF</b> 토글로 PT 단독 / PT+그룹 매출을 전환해 비교할 수 있습니다.
                </li>
            </ul>
        </div>
    </div>
);

const SuperAdminGuideStep = ({
    num,
    title,
    desc,
}: {
    num: number;
    title: string;
    desc: string;
}) => (
    <div
        style={{
            padding: 22,
            background: T.card,
            border: `1px solid ${T.border}`,
            borderRadius: 12,
        }}
    >
        <div
            style={{
                display: 'inline-flex',
                width: 32,
                height: 32,
                borderRadius: 16,
                background: T.blue,
                color: T.card,
                fontWeight: 700,
                fontSize: 15,
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 12,
            }}
        >
            {num}
        </div>
        <div style={{ fontSize: 16, fontWeight: 700, color: T.navy, marginBottom: 8 }}>{title}</div>
        <div style={{ fontSize: 14, color: T.text2, whiteSpace: 'pre-line', lineHeight: 1.6 }}>
            {desc}
        </div>
    </div>
);

export default SuperAdminTeamAnalyticsPage;
